/* Full sheet-fidelity DSR Manual Upload — mirrors the "DSR (MENS) 12 WEEKENDED"
 * xlsb layout: store identity + season/monthly targets + 5 weekly blocks with
 * 7 daily rows × 13 fields (SOH, MRP, Net, Qty, ASP, 7 categories, Total)
 * + weekly target + weekly achievement + MTD/STD totals + Competition tracker.
 */
import { useEffect, useMemo, useState } from "react";
import { http, fmtINR, fmtNum, fmtPct, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Save, RefreshCw, ChevronDown, ChevronRight } from "lucide-react";

const ZONE_OPTS = ["NORTH", "SOUTH", "EAST", "WEST", "CENTRAL"];
const GENDER_OPTS = ["MENS", "WOMENS", "KIDS", "UNISEX"];
const PAYROLL_OPTS = ["IKYA PAYROLL", "SPYKAR PAYROLL", "STORE PAYROLL", "CONTRACT"];
const STATUS_OPTS = ["", "OPEN", "CLOSED", "ON HOLD"];

// Category keys — labels EXACTLY as they appear in the sheet (row 5, cols 20-26)
const CATS = [
    { key: "mens_basic", label: "Mens Basic" },
    { key: "mens_fashion", label: "Mens Fashion" },
    { key: "trousers", label: "Trousers" },
    { key: "mens_shirt", label: "Mens Shirt" },
    { key: "mens_tshirt", label: "Mens T-Shirt" },
    { key: "mens_sweatshirt", label: "Mens Sweatshirt/Sweater/Flat knit" },
    { key: "mens_jackets", label: "Mens Jackets" },
];

// Daily fields — labels EXACTLY from sheet row 5 (cols 15-27 for each day)
const DAILY_FIELDS = [
    { key: "soh", label: "SOH" },
    { key: "mrp", label: "MRP sale" },
    { key: "net", label: "Net sales" },
    { key: "qty", label: "Qty" },
    { key: "asp", label: "ASP" },
    ...CATS,
    { key: "total", label: "Total" },
];

// Week labels — EXACTLY as sheet has them at row 4 cols 106/214/322/430/538
const WEEK_LABELS = ["44 WEEK", "45 WEEK", "46 WEEK", "47 WEEK", "48 WEEK"];
const WEEK_NUMBERS = [44, 45, 46, 47, 48];
const COMP_WEEK_LABELS = ["44 WEEK", "45 WEEK", "46 WEEK", "47 WEEK", "48 WEEK"];

// Prefilled dates per week — matches the FEB-26- sheet (row 4 daily-header dates).
// WK-1: only Feb 1 falls in the Feb sheet (WK-1 days 1-6 spill into JAN-26-).
// WK-2..WK-5: 7 consecutive dates each.
const WEEK_DEFAULT_DATES = {
    "FEB-26-": [
        ["", "", "", "", "", "", "2026-02-01"],
        ["2026-02-02", "2026-02-03", "2026-02-04", "2026-02-05", "2026-02-06", "2026-02-07", "2026-02-08"],
        ["2026-02-09", "2026-02-10", "2026-02-11", "2026-02-12", "2026-02-13", "2026-02-14", "2026-02-15"],
        ["2026-02-16", "2026-02-17", "2026-02-18", "2026-02-19", "2026-02-20", "2026-02-21", "2026-02-22"],
        ["2026-02-23", "2026-02-24", "2026-02-25", "2026-02-26", "2026-02-27", "2026-02-28", "2026-03-01"],
    ],
};

const emptyDay = (date = "") => ({
    date,
    soh: "", mrp: "", net: "", qty: "", asp: "",
    mens_basic: "", mens_fashion: "", trousers: "",
    mens_shirt: "", mens_tshirt: "", mens_sweatshirt: "", mens_jackets: "",
    total: "",
});

const emptyWeek = (idx, monthSheet = "FEB-26-") => {
    const dates = WEEK_DEFAULT_DATES[monthSheet]?.[idx] ?? ["", "", "", "", "", "", ""];
    return {
        label: WEEK_LABELS[idx],
        week_no: WEEK_NUMBERS[idx],
        days: dates.map((d) => emptyDay(d)),
        week_target: { soh: "", mrp: "", net: "", qty: "" },
    };
};

const emptyCompetition = () => ({
    dsr_link_name: "",
    staff_name: "",
    mrm_name: "",
    status: "",
    weeks: COMP_WEEK_LABELS.map((label) => ({ label, qty: "", mrp: "", value: "" })),
});

const emptyForm = () => ({
    // Store Identity — labels exactly as in sheet row 5 cols 0-8
    sr_no: "",
    location: "",
    work_location: "",
    state: "",
    zone: "NORTH",
    employee: "",
    gender: "MENS",
    payroll: "IKYA PAYROLL",
    rsm_asm: "",
    // Season & Monthly targets — row 5 cols 9-14
    season_mrp: "",
    season_net: "",
    season_qty: "",
    mrp_target: "",
    net_target: "",
    qty_target: "",
    // 5 weekly blocks
    weeks: [0, 1, 2, 3, 4].map((i) => emptyWeek(i, "FEB-26-")),
    // Competition tracker
    competition: emptyCompetition(),
    // Meta
    entry_date: new Date().toISOString().slice(0, 10),
    month_sheet: "FEB-26-",
    notes: "",
});

const n = (v) => Number(v || 0);

function sumWeek(week, key) {
    return week.days.reduce((a, d) => a + n(d[key]), 0);
}

function sumAllWeeks(weeks, key) {
    return weeks.reduce((a, w) => a + sumWeek(w, key), 0);
}

// Convert form (with string inputs) to backend payload (with numeric values)
function toPayload(form) {
    const numFields = [
        "sr_no", "season_mrp", "season_net", "season_qty",
        "mrp_target", "net_target", "qty_target",
    ];
    const payload = { ...form };
    numFields.forEach((f) => (payload[f] = n(form[f])));
    payload.weeks = form.weeks.map((w) => ({
        label: w.label,
        week_no: w.week_no,
        week_target: {
            soh: n(w.week_target.soh),
            mrp: n(w.week_target.mrp),
            net: n(w.week_target.net),
            qty: n(w.week_target.qty),
        },
        days: w.days.map((d) => {
            const obj = { date: d.date || null };
            DAILY_FIELDS.forEach((f) => (obj[f.key] = n(d[f.key])));
            return obj;
        }),
    }));
    payload.competition = {
        ...form.competition,
        weeks: form.competition.weeks.map((cw) => ({
            label: cw.label,
            qty: n(cw.qty),
            mrp: n(cw.mrp),
            value: n(cw.value),
        })),
    };
    return payload;
}

export default function DsrManualUpload() {
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [recent, setRecent] = useState([]);
    // All 5 week blocks open by default so the form mirrors the flat sheet layout
    const [openWeeks, setOpenWeeks] = useState({ 0: true, 1: true, 2: true, 3: true, 4: true });

    useEffect(() => { loadRecent(); }, []);

    const loadRecent = () => {
        http.get("/reports/dsr/manual", { params: { limit: 25 } }).then((r) => setRecent(r.data));
    };

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const setWeekDay = (wi, di, key, val) => {
        setForm((f) => {
            const weeks = f.weeks.map((w, i) => {
                if (i !== wi) return w;
                const days = w.days.map((d, j) => (j === di ? { ...d, [key]: val } : d));
                return { ...w, days };
            });
            return { ...f, weeks };
        });
    };

    const setWeekTarget = (wi, key, val) => {
        setForm((f) => {
            const weeks = f.weeks.map((w, i) =>
                i === wi ? { ...w, week_target: { ...w.week_target, [key]: val } } : w
            );
            return { ...f, weeks };
        });
    };

    const setComp = (k, v) => setForm((f) => ({ ...f, competition: { ...f.competition, [k]: v } }));

    const setCompWeek = (i, k, v) => {
        setForm((f) => {
            const weeks = f.competition.weeks.map((cw, j) => (j === i ? { ...cw, [k]: v } : cw));
            return { ...f, competition: { ...f.competition, weeks } };
        });
    };

    // ---- Live MTD/STD totals ----
    const mtd = useMemo(() => ({
        mrp: sumAllWeeks(form.weeks, "mrp"),
        net: sumAllWeeks(form.weeks, "net"),
        qty: sumAllWeeks(form.weeks, "qty"),
        ...Object.fromEntries(CATS.map((c) => [c.key, sumAllWeeks(form.weeks, c.key)])),
    }), [form.weeks]);

    const submit = async () => {
        if (!form.location.trim()) {
            toast.error("Location is required");
            return;
        }
        setSaving(true);
        try {
            await http.post("/reports/dsr/manual", toPayload(form));
            toast.success("DSR row saved", { description: `${form.location} · ${form.entry_date}` });
            setForm(emptyForm());
            setOpenWeeks({ 0: true, 1: true, 2: true, 3: true, 4: true });
            loadRecent();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-4" data-testid="dsr-manual-form">
            {/* Sheet source banner — makes it explicit these labels come from the source sheet */}
            <div
                data-testid="mu-sheet-banner"
                className="flex flex-wrap items-center justify-between gap-2 rounded-sm border border-primary/30 bg-primary/5 px-3 py-2 text-[11px]"
            >
                <div>
                    <span className="mlabel mr-2">Source sheet</span>
                    <span className="font-semibold">{form.month_sheet || "FEB-26-"}</span>
                    <span className="mx-2 text-muted-foreground">·</span>
                    <span className="text-muted-foreground">
                        Labels, week headers (44 WEEK … 48 WEEK), day dates and category names are
                        taken verbatim from the workbook.
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <span className="mlabel">Change month:</span>
                    <select
                        data-testid="mu-month-select"
                        value={form.month_sheet}
                        onChange={(e) => {
                            const ms = e.target.value;
                            setForm((f) => ({
                                ...f,
                                month_sheet: ms,
                                weeks: [0, 1, 2, 3, 4].map((i) => {
                                    const dates = WEEK_DEFAULT_DATES[ms]?.[i] ?? ["", "", "", "", "", "", ""];
                                    return {
                                        ...f.weeks[i],
                                        days: f.weeks[i].days.map((d, di) => ({ ...d, date: dates[di] || d.date })),
                                    };
                                }),
                            }));
                        }}
                        className="rounded-sm border border-border bg-background px-2 py-1 text-[11px]"
                    >
                        <option value="FEB-26-">FEB-26-</option>
                    </select>
                </div>
            </div>

            {/* ---- Store Identity ---- */}
            <Section title="Store Identity">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                    <NumFieldSm label="Sr.no" testid="mu-sr_no" value={form.sr_no} onChange={(v) => set("sr_no", v)} />
                    <TextField label="Location *" required testid="mu-location" value={form.location} onChange={(v) => set("location", v)} placeholder="CHUNMUN" />
                    <TextField label="WorkLocation" testid="mu-work_location" value={form.work_location} onChange={(v) => set("work_location", v)} placeholder="PACIFIC MALL" />
                    <TextField label="PTState" testid="mu-state" value={form.state} onChange={(v) => set("state", v)} placeholder="DELHI" />
                    <SelectField label="Zone" testid="mu-zone" value={form.zone} options={ZONE_OPTS} onChange={(v) => set("zone", v)} />
                    <TextField label="Employee_Name" testid="mu-employee" value={form.employee} onChange={(v) => set("employee", v)} />
                    <SelectField label="GENDER" testid="mu-gender" value={form.gender} options={GENDER_OPTS} onChange={(v) => set("gender", v)} />
                    <SelectField label="PAYROLL" testid="mu-payroll" value={form.payroll} options={PAYROLL_OPTS} onChange={(v) => set("payroll", v)} />
                    <TextField label="RSM / ASM" testid="mu-rsm_asm" value={form.rsm_asm} onChange={(v) => set("rsm_asm", v)} />
                </div>
            </Section>

            {/* ---- Season Target + Monthly Target ---- */}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Section title="Season Target">
                    <div className="grid grid-cols-3 gap-3">
                        <NumFieldSm label="MRP sale" testid="mu-season_mrp" value={form.season_mrp} onChange={(v) => set("season_mrp", v)} />
                        <NumFieldSm label="Net sales" testid="mu-season_net" value={form.season_net} onChange={(v) => set("season_net", v)} />
                        <NumFieldSm label="Qty" testid="mu-season_qty" value={form.season_qty} onChange={(v) => set("season_qty", v)} />
                    </div>
                </Section>
                <Section title="Monthly Target">
                    <div className="grid grid-cols-3 gap-3">
                        <NumFieldSm label="MRP sale MTH TGT" testid="mu-mrp_target" value={form.mrp_target} onChange={(v) => set("mrp_target", v)} />
                        <NumFieldSm label="Net sales MTH TGT" testid="mu-net_target" value={form.net_target} onChange={(v) => set("net_target", v)} />
                        <NumFieldSm label="Qty MTH TGT" testid="mu-qty_target" value={form.qty_target} onChange={(v) => set("qty_target", v)} />
                    </div>
                </Section>
            </div>

            {/* ---- 5 Weekly Blocks ---- */}
            <div className="space-y-2">
                {form.weeks.map((w, wi) => (
                    <WeekBlock
                        key={w.label}
                        week={w}
                        idx={wi}
                        open={!!openWeeks[wi]}
                        onToggle={() => setOpenWeeks((prev) => ({ ...prev, [wi]: !prev[wi] }))}
                        onDayChange={(di, key, val) => setWeekDay(wi, di, key, val)}
                        onTargetChange={(key, val) => setWeekTarget(wi, key, val)}
                    />
                ))}
            </div>

            {/* ---- MTD Totals (MONTHLY ACHD — sheet row 4 col 611) ---- */}
            <Section title="MONTHLY ACHD">
                <div className="grid grid-cols-2 gap-2 md:grid-cols-7">
                    <ReadOnlyCell label="SOH1" value="R" testid="mtd-soh" />
                    <ReadOnlyCell label="MRP sale Ach" value={fmtINR(mtd.mrp)} testid="mtd-mrp" />
                    <ReadOnlyCell label="Net sales Ach" value={fmtINR(mtd.net)} testid="mtd-net" />
                    <ReadOnlyCell label="Qty Ach WK" value={fmtNum(mtd.qty)} testid="mtd-qty" />
                    <ReadOnlyCell
                        label="ASP"
                        value={mtd.qty ? fmtNum(Math.round(mtd.net / mtd.qty)) : "—"}
                        testid="mtd-asp"
                    />
                    {CATS.map((c) => (
                        <ReadOnlyCell key={c.key} label={`${c.label} Total`} value={fmtNum(mtd[c.key])} testid={`mtd-${c.key}`} />
                    ))}
                    <ReadOnlyCell
                        label="Total"
                        value={fmtNum(CATS.reduce((a, c) => a + mtd[c.key], 0))}
                        testid="mtd-total"
                    />
                </div>
                {/* Ach% strip vs monthly target */}
                <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-3">
                    <Stat
                        label="MRP Ach %"
                        value={form.mrp_target ? fmtPct((mtd.mrp / n(form.mrp_target)) * 100) : "—"}
                        tone={mtd.mrp >= n(form.mrp_target) ? "green" : "amber"}
                    />
                    <Stat
                        label="Net Ach %"
                        value={form.net_target ? fmtPct((mtd.net / n(form.net_target)) * 100) : "—"}
                        tone={mtd.net >= n(form.net_target) ? "green" : "amber"}
                    />
                    <Stat
                        label="Qty Ach %"
                        value={form.qty_target ? fmtPct((mtd.qty / n(form.qty_target)) * 100) : "—"}
                        tone={mtd.qty >= n(form.qty_target) ? "green" : "amber"}
                    />
                </div>
            </Section>

            {/* ---- Short fall (STD) — sheet row 4 col 624 ---- */}
            <Section title="Short fall">
                <div className="grid grid-cols-2 gap-2 md:grid-cols-6">
                    <ReadOnlyCell label="SOH" value="R" testid="std-soh" />
                    <ReadOnlyCell
                        label="MRP sale"
                        value={fmtINR(n(form.mrp_target) - mtd.mrp)}
                        tone={n(form.mrp_target) - mtd.mrp > 0 ? "red" : "green"}
                        testid="std-mrp"
                    />
                    <ReadOnlyCell
                        label="Net sales"
                        value={fmtINR(n(form.net_target) - mtd.net)}
                        tone={n(form.net_target) - mtd.net > 0 ? "red" : "green"}
                        testid="std-net"
                    />
                    <ReadOnlyCell
                        label="Qty"
                        value={fmtNum(n(form.qty_target) - mtd.qty)}
                        tone={n(form.qty_target) - mtd.qty > 0 ? "red" : "green"}
                        testid="std-qty"
                    />
                    {CATS.map((c) => (
                        <ReadOnlyCell
                            key={c.key}
                            label={`${c.label} Total`}
                            value={fmtNum(-mtd[c.key])}
                            testid={`std-${c.key}`}
                        />
                    ))}
                    <ReadOnlyCell
                        label="Total"
                        value={fmtNum(-CATS.reduce((a, c) => a + mtd[c.key], 0))}
                        testid="std-total"
                    />
                </div>
            </Section>

            {/* ---- Competition ---- */}
            <Section title="Competition Tracking">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                    <TextField label="DSR LINK NAME" testid="mu-comp-link" value={form.competition.dsr_link_name} onChange={(v) => setComp("dsr_link_name", v)} />
                    <TextField label="STAFF NAME" testid="mu-comp-staff" value={form.competition.staff_name} onChange={(v) => setComp("staff_name", v)} />
                    <TextField label="MRM NAME" testid="mu-comp-mrm" value={form.competition.mrm_name} onChange={(v) => setComp("mrm_name", v)} />
                    <SelectField label="STATUS" testid="mu-comp-status" value={form.competition.status} options={STATUS_OPTS} onChange={(v) => setComp("status", v)} />
                </div>
                <div className="mt-3 overflow-x-auto">
                    <table className="w-full text-[12px]">
                        <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
                            <tr>
                                <th className="px-2 py-1 text-left">Week</th>
                                <th className="px-2 py-1 text-right">QTY</th>
                                <th className="px-2 py-1 text-right">MRP</th>
                                <th className="px-2 py-1 text-right">VALUE</th>
                            </tr>
                        </thead>
                        <tbody>
                            {form.competition.weeks.map((cw, i) => (
                                <tr key={cw.label} className="border-t border-border">
                                    <td className="px-2 py-1 font-medium">{cw.label}</td>
                                    <td className="px-2 py-1 text-right">
                                        <MiniNumInput testid={`mu-comp-qty-${i}`} value={cw.qty} onChange={(v) => setCompWeek(i, "qty", v)} />
                                    </td>
                                    <td className="px-2 py-1 text-right">
                                        <MiniNumInput testid={`mu-comp-mrp-${i}`} value={cw.mrp} onChange={(v) => setCompWeek(i, "mrp", v)} />
                                    </td>
                                    <td className="px-2 py-1 text-right">
                                        <MiniNumInput testid={`mu-comp-value-${i}`} value={cw.value} onChange={(v) => setCompWeek(i, "value", v)} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Section>

            {/* ---- Meta ---- */}
            <Section title="Meta">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <label className="block">
                        <div className="mlabel mb-1">Entry Date</div>
                        <input
                            type="date"
                            data-testid="mu-entry_date"
                            value={form.entry_date}
                            onChange={(e) => set("entry_date", e.target.value)}
                            className="w-full rounded-sm border border-border bg-background px-2 py-1.5 text-[12px]"
                        />
                    </label>
                    <TextField label="Month Sheet" testid="mu-month_sheet" value={form.month_sheet} onChange={(v) => set("month_sheet", v)} placeholder="FEB-26-" />
                    <TextField label="Notes" testid="mu-notes" value={form.notes} onChange={(v) => set("notes", v)} />
                </div>
            </Section>

            {/* ---- Actions ---- */}
            <div className="flex items-center justify-end gap-2">
                <button
                    data-testid="mu-reset"
                    onClick={() => { setForm(emptyForm()); setOpenWeeks({ 0: true, 1: true, 2: true, 3: true, 4: true }); }}
                    className="inline-flex items-center gap-1.5 rounded-sm border border-border bg-background px-3 py-1.5 text-[12px] font-medium hover:bg-muted/40"
                >
                    <RefreshCw className="h-3.5 w-3.5" /> Reset
                </button>
                <button
                    data-testid="mu-submit"
                    disabled={saving}
                    onClick={submit}
                    className="inline-flex items-center gap-1.5 rounded-sm bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
                >
                    <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save Entry"}
                </button>
            </div>

            {/* ---- Recent entries ---- */}
            <RecentTable rows={recent} />
        </div>
    );
}

// ---------------------------------------------------------------------------
// WeekBlock — collapsible, 7 days × 13 fields grid + weekly target + achievement
// ---------------------------------------------------------------------------
function WeekBlock({ week, idx, open, onToggle, onDayChange, onTargetChange }) {
    const weekAch = {
        mrp: sumWeek(week, "mrp"), net: sumWeek(week, "net"), qty: sumWeek(week, "qty"),
        ...Object.fromEntries(CATS.map((c) => [c.key, sumWeek(week, c.key)])),
    };
    const achPct = week.week_target.mrp ? (weekAch.mrp / n(week.week_target.mrp)) * 100 : 0;

    return (
        <div className="rounded-sm border border-border bg-card" data-testid={`week-block-${idx}`}>
            <button
                onClick={onToggle}
                data-testid={`week-toggle-${idx}`}
                className="flex w-full items-center justify-between border-b border-border px-3 py-2 text-left hover:bg-muted/30"
            >
                <div className="flex items-center gap-2">
                    {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                    <span className="text-[12px] font-semibold">{week.label}</span>
                </div>
                <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                    <span>MRP <span className="mono-num font-semibold text-foreground">{fmtINR(weekAch.mrp)}</span></span>
                    <span>Net <span className="mono-num font-semibold text-foreground">{fmtINR(weekAch.net)}</span></span>
                    <span>Qty <span className="mono-num font-semibold text-foreground">{fmtNum(weekAch.qty)}</span></span>
                    {week.week_target.mrp && (
                        <span className={achPct >= 100 ? "text-emerald-600" : achPct >= 70 ? "text-amber-600" : "text-red-500"}>
                            Ach <span className="mono-num font-semibold">{fmtPct(achPct)}</span>
                        </span>
                    )}
                </div>
            </button>

            {open && (
                <div className="p-3">
                    <div className="overflow-x-auto">
                        <table className="w-full text-[11px]">
                            <thead className="bg-muted/40 text-[9px] uppercase tracking-wider text-muted-foreground">
                                <tr>
                                    <th className="px-2 py-1 text-left">Day</th>
                                    <th className="px-2 py-1 text-left">Date</th>
                                    {DAILY_FIELDS.map((f) => (
                                        <th key={f.key} className="px-1 py-1 text-right whitespace-nowrap">{f.label}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {week.days.map((d, di) => (
                                    <tr key={di} className="border-t border-border">
                                        <td className="px-2 py-1 text-muted-foreground">D{di + 1}</td>
                                        <td className="px-1 py-1">
                                            <input
                                                type="date"
                                                data-testid={`w${idx}-d${di}-date`}
                                                value={d.date}
                                                onChange={(e) => onDayChange(di, "date", e.target.value)}
                                                className="w-[120px] rounded-sm border border-border bg-background px-1 py-0.5 text-[11px]"
                                            />
                                        </td>
                                        {DAILY_FIELDS.map((f) => (
                                            <td key={f.key} className="px-0.5 py-1">
                                                <MiniNumInput
                                                    testid={`w${idx}-d${di}-${f.key}`}
                                                    value={d[f.key]}
                                                    onChange={(v) => onDayChange(di, f.key, v)}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                                {/* Week Achievement row (auto-computed) */}
                                <tr className="border-t-2 border-border bg-muted/30 font-semibold">
                                    <td colSpan={2} className="px-2 py-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                                        Week Ach (auto)
                                    </td>
                                    <td className="mono-num px-1 py-1 text-right text-muted-foreground">—</td>
                                    <td className="mono-num px-1 py-1 text-right">{fmtNum(weekAch.mrp)}</td>
                                    <td className="mono-num px-1 py-1 text-right">{fmtNum(weekAch.net)}</td>
                                    <td className="mono-num px-1 py-1 text-right">{fmtNum(weekAch.qty)}</td>
                                    <td className="mono-num px-1 py-1 text-right text-muted-foreground">
                                        {weekAch.qty ? Math.round(weekAch.net / weekAch.qty) : "—"}
                                    </td>
                                    {CATS.map((c) => (
                                        <td key={c.key} className="mono-num px-1 py-1 text-right">{fmtNum(weekAch[c.key])}</td>
                                    ))}
                                    <td className="mono-num px-1 py-1 text-right">
                                        {fmtNum(CATS.reduce((a, c) => a + weekAch[c.key], 0))}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* Weekly Target — labels EXACTLY from sheet row 5 cols 119-122 (WK-N convention) */}
                    <div className="mt-3 rounded-sm border border-border bg-muted/20 p-2">
                        <div className="mlabel mb-1">Weekly Target (WK-{idx + 1})</div>
                        <div className="grid grid-cols-4 gap-2">
                            <MiniField label={`SOH TGT WK-${idx + 1}`}>
                                <MiniNumInput testid={`w${idx}-tgt-soh`} value={week.week_target.soh} onChange={(v) => onTargetChange("soh", v)} />
                            </MiniField>
                            <MiniField label={`MRP sale TGT WK-${idx + 1}`}>
                                <MiniNumInput testid={`w${idx}-tgt-mrp`} value={week.week_target.mrp} onChange={(v) => onTargetChange("mrp", v)} />
                            </MiniField>
                            <MiniField label={`Net sales TGT Wk-${idx + 1}`}>
                                <MiniNumInput testid={`w${idx}-tgt-net`} value={week.week_target.net} onChange={(v) => onTargetChange("net", v)} />
                            </MiniField>
                            <MiniField label={`Qty TGT WK-${idx + 1}`}>
                                <MiniNumInput testid={`w${idx}-tgt-qty`} value={week.week_target.qty} onChange={(v) => onTargetChange("qty", v)} />
                            </MiniField>
                        </div>
                    </div>

                    {/* Week Achievement — header EXACTLY as sheet row 4 col 106/214/… (e.g. "44 WEEK") */}
                    <div className="mt-3 rounded-sm border border-emerald-600/40 bg-emerald-50/40 p-2 dark:bg-emerald-900/10">
                        <div className="mlabel mb-1 text-emerald-700 dark:text-emerald-400">
                            {week.label}
                        </div>
                        <div className="grid grid-cols-2 gap-2 md:grid-cols-7">
                            <ReadOnlyCell label="SOH1" value="R" testid={`w${idx}-ach-soh1`} />
                            <ReadOnlyCell label="MRP sale Ach" value={fmtNum(weekAch.mrp)} testid={`w${idx}-ach-mrp`} />
                            <ReadOnlyCell label="Net sales Ach" value={fmtNum(weekAch.net)} testid={`w${idx}-ach-net`} />
                            <ReadOnlyCell label="Qty Ach WK" value={fmtNum(weekAch.qty)} testid={`w${idx}-ach-qty`} />
                            <ReadOnlyCell
                                label="ASP"
                                value={weekAch.qty ? fmtNum(Math.round(weekAch.net / weekAch.qty)) : "—"}
                                testid={`w${idx}-ach-asp`}
                            />
                            {CATS.map((c) => (
                                <ReadOnlyCell
                                    key={c.key}
                                    label={`${c.label} Total`}
                                    value={fmtNum(weekAch[c.key])}
                                    testid={`w${idx}-ach-${c.key}`}
                                />
                            ))}
                            <ReadOnlyCell
                                label="Total"
                                value={fmtNum(CATS.reduce((a, c) => a + weekAch[c.key], 0))}
                                testid={`w${idx}-ach-total`}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Recent entries table
// ---------------------------------------------------------------------------
function RecentTable({ rows }) {
    return (
        <div className="overflow-hidden rounded-sm border border-border bg-card" data-testid="mu-recent-table">
            <div className="border-b border-border bg-muted/40 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Recent Manual Entries · {rows.length}
            </div>
            <div className="max-h-[360px] overflow-auto">
                <table className="w-full text-[12px]">
                    <thead className="sticky top-0 z-10 bg-muted/60 text-[10px] uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="px-3 py-2 text-left">Entry Date</th>
                            <th className="px-3 py-2 text-left">Location</th>
                            <th className="px-3 py-2 text-left">State</th>
                            <th className="px-3 py-2 text-left">Zone</th>
                            <th className="px-3 py-2 text-left">RSM/ASM</th>
                            <th className="px-3 py-2 text-right">MTD MRP</th>
                            <th className="px-3 py-2 text-right">MTD Net</th>
                            <th className="px-3 py-2 text-right">MTD Qty</th>
                            <th className="px-3 py-2 text-right">MRP Tgt</th>
                            <th className="px-3 py-2 text-right">Ach %</th>
                            <th className="px-3 py-2 text-left">By</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => {
                            const pct = r.mrp_target ? (r.mrp_sale / r.mrp_target) * 100 : 0;
                            return (
                                <tr key={r.id} className="border-t border-border hover:bg-muted/40">
                                    <td className="mono-num px-3 py-1.5 text-muted-foreground">{r.entry_date || "—"}</td>
                                    <td className="px-3 py-1.5 font-medium">{r.location}</td>
                                    <td className="px-3 py-1.5">{r.state || "—"}</td>
                                    <td className="px-3 py-1.5">{r.zone || "—"}</td>
                                    <td className="px-3 py-1.5">{r.rsm_asm || "—"}</td>
                                    <td className="mono-num px-3 py-1.5 text-right">{fmtINR(r.mrp_sale)}</td>
                                    <td className="mono-num px-3 py-1.5 text-right">{fmtINR(r.net_sales)}</td>
                                    <td className="mono-num px-3 py-1.5 text-right">{fmtNum(r.qty)}</td>
                                    <td className="mono-num px-3 py-1.5 text-right text-muted-foreground">{fmtINR(r.mrp_target)}</td>
                                    <td
                                        className={
                                            "mono-num px-3 py-1.5 text-right font-semibold " +
                                            (pct >= 100 ? "text-emerald-600" : pct >= 70 ? "" : pct > 0 ? "text-red-500" : "text-muted-foreground")
                                        }
                                    >
                                        {r.mrp_target ? fmtPct(pct) : "—"}
                                    </td>
                                    <td className="px-3 py-1.5 text-[11px] text-muted-foreground">{r.created_by_name || "—"}</td>
                                </tr>
                            );
                        })}
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={11} className="px-3 py-10 text-center text-[12px] text-muted-foreground">
                                    No manual entries yet.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Reusable field primitives
// ---------------------------------------------------------------------------
function Section({ title, children }) {
    return (
        <div className="rounded-sm border border-border bg-card p-4">
            <div className="mlabel mb-3">{title}</div>
            {children}
        </div>
    );
}

function TextField({ label, testid, value, onChange, placeholder, required }) {
    return (
        <label className="block">
            <div className="mlabel mb-1">{label}{required && <span className="ml-0.5 text-red-500">*</span>}</div>
            <input
                type="text"
                data-testid={testid}
                value={value || ""}
                placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
                className="w-full rounded-sm border border-border bg-background px-2 py-1.5 text-[12px]"
            />
        </label>
    );
}

function SelectField({ label, testid, value, options, onChange }) {
    return (
        <label className="block">
            <div className="mlabel mb-1">{label}</div>
            <select
                data-testid={testid}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                className="w-full rounded-sm border border-border bg-background px-2 py-1.5 text-[12px]"
            >
                {options.map((o) => (
                    <option key={o} value={o}>{o || "—"}</option>
                ))}
            </select>
        </label>
    );
}

function NumFieldSm({ label, testid, value, onChange }) {
    return (
        <label className="block">
            <div className="mlabel mb-1">{label}</div>
            <input
                type="number"
                step="any"
                min="0"
                data-testid={testid}
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                className="mono-num w-full rounded-sm border border-border bg-background px-2 py-1.5 text-[12px]"
            />
        </label>
    );
}

function MiniField({ label, children }) {
    return (
        <label className="block">
            <div className="mlabel mb-0.5 text-[9px]">{label}</div>
            {children}
        </label>
    );
}

function MiniNumInput({ value, onChange, testid }) {
    return (
        <input
            type="number"
            step="any"
            min="0"
            data-testid={testid}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            className="mono-num w-[70px] rounded-sm border border-border bg-background px-1 py-0.5 text-right text-[11px] outline-none focus:border-primary/60"
        />
    );
}

function Stat({ label, value, tone }) {
    const c = tone === "green" ? "text-emerald-600" : tone === "amber" ? "text-amber-600" : tone === "red" ? "text-red-500" : "";
    return (
        <div className="rounded-sm border border-border bg-muted/30 p-3">
            <div className="mlabel">{label}</div>
            <div className={`mono-num mt-1 text-lg font-semibold ${c}`}>{value}</div>
        </div>
    );
}

function ReadOnlyCell({ label, value, testid, tone }) {
    const c = tone === "green" ? "text-emerald-600" : tone === "amber" ? "text-amber-600" : tone === "red" ? "text-red-500" : "";
    return (
        <div className="rounded-sm border border-border bg-background p-1.5" data-testid={testid}>
            <div className="mlabel text-[9px] leading-tight">{label}</div>
            <div className={`mono-num mt-0.5 text-[12px] font-semibold ${c}`}>{value}</div>
        </div>
    );
}
